import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { Monitor, Smartphone, Puzzle, Boxes, ArrowLeft, ArrowRight } from 'lucide-react';
import { FadeIn } from './RevealText';
import ArrowMark from './ArrowMark';
import { LetterRoll } from './WorkGrid';
import { useTouchActive } from './useTouchActive';
import { EASE_IN_OUT, EASE_OUT } from './easing';
import { trackInteraction } from '../../analytics';

/**
 * VibeCoding — the software I build around the motion work.
 *
 * Each project is a spread: a column of words (what it is, the platforms it
 * runs on, three plain sentences about what it does) beside a slideshow of its
 * real interface, one screen at a time, captioned. The slideshow plays the big
 * screens, then the phone version (every one of these works on a phone, and two
 * ship as native iOS apps), then the details. Sides alternate down the section.
 *
 * The motion is the site's: the slideshow wipes in like a work card, the title
 * rolls into the accent colour, and a phone (which never hovers) gets the roll
 * when the card reaches mid-screen.
 *
 * Every image carries its real size so nothing shifts as it loads, and
 * descriptive alt text. The same projects are described in index.html's
 * JSON-LD and in sitemap.xml.
 */

interface Media {
  src: string;
  w: number;
  h: number;
  alt: string;
  caption: string;
  /** muted loop shown over the still, played only while on screen */
  video?: string;
  /** 'float' sits a cut-out shot on the project's glow, 'phone' frames it, 'robots' is Farmly's face wall */
  fit?: 'cover' | 'float' | 'phone' | 'robots';
  position?: string;
}

type Platform = 'web' | 'phone' | 'ios' | 'extension' | 'plugins';

interface Tool {
  id: string;
  name: string;
  /** two words for the project index */
  kind: string;
  tagline: string;
  platforms: Platform[];
  /** the glow behind phone and cut-out tiles */
  accent: string;
  /** one shot, or several for a switcher */
  hero: Media[];
  phone: Media;
  tiles: Media[];
  highlights: { title: string; text: string }[];
  footer: string;
  link?: { label: string; url: string };
}

const V = '/vibecoding';

const TOOLS: Tool[] = [
  {
    id: 'preshow',
    name: 'Preshow',
    kind: 'LED review',
    tagline: 'See your video on the LED wall before anyone builds the stage.',
    platforms: ['web', 'phone'],
    accent: '#3b6cf6',
    hero: [
      {
        src: `${V}/preshow-editor.webp`,
        w: 1800,
        h: 1125,
        alt: 'Preshow editor: blue lava content on a curved LED wall and two totems, a running line along the stage front, a timeline per screen and client notes beside the picture',
        caption: 'Every screen on the stage, playing the real content.',
      },
    ],
    phone: {
      src: `${V}/preshow-phone.webp`,
      w: 600,
      h: 1299,
      alt: 'Preshow client review on a phone: the stage playing, with an Approve button and an Add note button',
      caption: 'Clients review and approve from a phone.',
    },
    tiles: [
      {
        src: `${V}/preshow-cameras.webp`,
        video: `${V}/preshow-cameras.mp4`,
        w: 1000,
        h: 753,
        alt: 'The camera flying between saved views of a stage: a curved LED wall, two glowing totems and a running line',
        caption: 'Fly the saved cameras around the room.',
      },
      {
        src: `${V}/preshow-compare.webp`,
        w: 1200,
        h: 497,
        alt: 'Two versions of the main wall video compared in a split view, warm orange against blue',
        caption: 'Two versions, split or wiped.',
        fit: 'float',
      },
      {
        src: `${V}/preshow-share.webp`,
        w: 760,
        h: 696,
        alt: 'Preshow share dialog: a review link to copy, guests or sign-in, and when it expires',
        caption: 'One link to share. No account needed.',
        fit: 'float',
      },
    ],
    highlights: [
      { title: 'On the real stage', text: 'Drop in the venue, and every video plays on the screens it was made for.' },
      { title: 'Review from a link', text: 'Clients watch, leave notes on the exact frame and approve, without signing up.' },
      { title: 'Anywhere', text: 'The whole review works on a phone, so sign-off can happen on the way to the venue.' },
    ],
    footer: 'Designed and built solo · Live',
    link: { label: 'preshow.link', url: 'https://preshow.link' },
  },
  {
    id: 'farmly',
    name: 'Farmly',
    kind: 'Render farm',
    tagline: 'The render farm manager that keeps Toy Robot Media’s 29 machines busy.',
    platforms: ['web', 'phone', 'plugins'],
    accent: '#22c7e6',
    hero: [
      {
        src: `${V}/farmly-overview.webp`,
        w: 1400,
        h: 875,
        alt: 'Farmly overview: frames rendered per day, frames per machine, and every machine on the farm working on one job',
        caption: 'The whole farm at a glance.',
      },
    ],
    phone: {
      src: `${V}/farmly-phone.webp`,
      w: 390,
      h: 844,
      alt: 'Farmly on a phone: machines up, frames in the last day, failures and licence seats',
      caption: 'Checking on the farm from a phone.',
    },
    tiles: [
      {
        src: `${V}/robots/robot-01.webp`,
        w: 192,
        h: 192,
        alt: 'A wall of cartoon robot faces, the profile pictures people pick in Farmly',
        caption: 'Everyone on the farm picks a robot face.',
        fit: 'robots',
      },
      {
        src: `${V}/farmly-analytics.webp`,
        w: 1400,
        h: 875,
        alt: 'Farmly analytics: render hours per day, by project and by app',
        caption: 'Where the render time went.',
      },
    ],
    highlights: [
      { title: 'Renders that finish', text: 'It catches the problems that used to fail jobs overnight, and works around them.' },
      { title: 'Send it from the app', text: 'Artists send renders straight from Cinema 4D and After Effects.' },
      { title: 'On any screen', text: 'Progress, previews and every machine’s health, from a desk or a phone.' },
    ],
    footer: 'Designed and built solo · Replaced Deadline',
  },
  {
    id: 'stitchr',
    name: 'Stitchr',
    kind: 'Resale shop',
    tagline: 'List once, sell everywhere: one calm place for a secondhand shop.',
    platforms: ['web', 'ios', 'extension'],
    accent: '#f0703a',
    hero: [
      {
        src: `${V}/stitchr-home.webp`,
        w: 1800,
        h: 1125,
        alt: 'Stitchr: list once, sell everywhere, across Poshmark, Depop, eBay, Mercari and Vinted',
        caption: 'One shop, five marketplaces.',
      },
    ],
    phone: {
      src: `${V}/stitchr-ship.webp`,
      w: 640,
      h: 849,
      alt: 'Stitchr iPhone app Ship screen: sold items with shipping labels to print and a Mark shipped button',
      caption: 'The iPhone app: print labels, mark shipped.',
    },
    tiles: [
      {
        src: `${V}/stitchr-widget.webp`,
        w: 640,
        h: 530,
        alt: 'Stitchr home-screen widget: 12 orders to ship and today’s sales',
        caption: 'A home-screen widget: what to ship, what sold.',
        fit: 'float',
      },
      {
        src: `${V}/stitchr-shortcuts.webp`,
        w: 640,
        h: 690,
        alt: 'Stitchr app quick actions on the iPhone home screen: New item, Ship, Pack',
        caption: 'New item, Ship and Pack, from the app icon.',
        fit: 'float',
      },
      {
        src: `${V}/stitchr-needs.webp`,
        w: 640,
        h: 870,
        alt: 'Stitchr list of items that need attention: slow sellers, missing weights, missing bins',
        caption: 'Finds what needs attention in 1,600+ items.',
        fit: 'float',
      },
    ],
    highlights: [
      { title: 'One listing, five shops', text: 'Photos become a listing that goes up on Poshmark, Depop, eBay, Mercari and Vinted.' },
      { title: 'Sold once, gone everywhere', text: 'When something sells, it comes down from the other shops by itself.' },
      { title: 'A real iPhone app', text: 'Shipping labels, today’s sales and what needs attention, with a home-screen widget.' },
    ],
    footer: 'Designed and built solo · Runs a real shop',
  },
  {
    id: 'henrys-journey',
    name: "Henry's Journey",
    kind: 'Puzzle game',
    tagline: 'A calm puzzle game I made for my son. Plan every move, press play, bring Henry home.',
    platforms: ['web', 'ios'],
    accent: '#8a6cf0',
    hero: [
      ['factory', 'The Factory', 14, 'Conveyor belts carry Henry across a pink factory floor'],
      ['abyss', 'The Abyss', 25, 'Night-time islands where walkers patrol and timing is everything'],
      ['dunes', 'Sandstorm Dunes', 46, 'Desert islands with boulders, cacti and warp pads'],
      ['snowy-peak', 'Snowy Peak', 50, 'Icy islands where Henry slides until something stops him'],
      ['crystal-caves', 'Crystal Caves', 66, 'Purple crystal caves with keys, locks and warp pads'],
      ['sunset-shores', 'Sunset Shores', 73, 'Teal shores where the tide floods tiles every few moves'],
    ].map(([slug, world, level, what]) => ({
      src: `${V}/henrys-journey-${slug}.webp`,
      w: 1800,
      h: 1125,
      alt: `Henry's Journey, ${world}, level ${level}: ${String(what).toLowerCase()}`,
      caption: `${world}, level ${level}. ${what}.`,
    })),
    phone: {
      src: `${V}/henrys-journey-phone.webp`,
      w: 600,
      h: 1299,
      alt: "Henry's Journey on an iPhone: Crystal Caves, level 66",
      caption: 'Crystal Caves on an iPhone.',
    },
    tiles: [
      {
        src: '/thumbs/henrys-journey-snow-run.webp',
        video: '/thumbs/henrys-journey-snow-run.mp4',
        w: 1200,
        h: 900,
        alt: "Henry's Journey gameplay: Henry runs a planned route across a snowy island",
        caption: 'Plan the route, then watch it play out.',
      },
      {
        src: `${V}/henrys-journey-home.webp`,
        w: 1800,
        h: 1125,
        alt: "Henry's Journey home screen: Henry and Mila on a meadow island, with Play and Choose a level",
        caption: 'Home, on the first meadow.',
        position: '88% center',
      },
      {
        src: `${V}/henrys-journey-shores-phone.webp`,
        w: 600,
        h: 1299,
        alt: "Henry's Journey on a phone, Sunset Shores level 73: Henry and Mila trade jokes in speech bubbles",
        caption: 'Henry and Mila chat while you think.',
        fit: 'phone',
      },
    ],
    highlights: [
      { title: 'Think first, then play', text: 'You plan the whole route, then watch Henry walk it. The fun is in thinking ahead.' },
      { title: 'Ten different worlds', text: 'Each one looks different and adds a new idea: belts, ice, tides, keys, lava, wind.' },
      { title: 'Something new every day', text: 'Five fresh islands every morning, races with friends, and islands players build themselves.' },
    ],
    footer: 'Designed and built solo · Free to play',
  },
];

const ROBOTS = ['01', '03', '04', '05', '06', '07', '08', '10', '11', '13', '14', '16'];

const PLATFORM: Record<Platform, { label: string; Icon: React.FC<{ size?: number; className?: string }> }> = {
  web: { label: 'Web', Icon: Monitor },
  phone: { label: 'Works on phones', Icon: Smartphone },
  ios: { label: 'Native iOS app', Icon: Smartphone },
  extension: { label: 'Chrome extension', Icon: Puzzle },
  plugins: { label: 'C4D & AE plugins', Icon: Boxes },
};

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const glow = (accent: string) =>
  `radial-gradient(90% 70% at 50% 108%, ${accent}66, transparent 70%), radial-gradient(60% 50% at 50% 0%, ${accent}1f, transparent 70%), #12141d`;

/** A project's slides, in the order a visitor should meet them: the big screen, the phone, the details. */
const slidesOf = (t: Tool): Media[] => [...t.hero, { ...t.phone, fit: 'phone' }, ...t.tiles];

/** How long a slide holds before the next: a loop gets time to show what it does. */
const dwellOf = (m: Media) => (m.video ? 9 : 4.8);

/**
 * A muted loop that plays only while its slide is showing and on screen.
 * preload="none" until then, so a visitor who never gets here never downloads it.
 */
const SlideVideo: React.FC<{ m: Media; active: boolean }> = ({ m, active }) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const inView = useInView(ref, { amount: 0.35 });

  useEffect(() => {
    const v = ref.current;
    if (!v || reducedMotion()) return;
    if (active && inView) {
      if (v.preload !== 'auto') v.preload = 'auto';
      v.currentTime = 0;
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [active, inView]);

  return (
    <video
      ref={ref}
      src={m.video}
      poster={m.src}
      aria-label={m.alt}
      muted
      loop
      playsInline
      preload="none"
      onPlaying={() => setReady(true)}
      className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${ready ? 'opacity-100' : 'opacity-0'}`}
    />
  );
};

/** Farmly's people, as the robots they picked. They lift one after another when the slide arrives. */
const RobotWall: React.FC<{ alt: string; active: boolean }> = ({ alt, active }) => (
  <div role="img" aria-label={alt} className="absolute inset-0 grid place-items-center p-[7%]">
    <div className="grid grid-cols-6 gap-[3%] w-full max-w-[720px]">
      {ROBOTS.map((n, i) => (
        <img
          key={n}
          src={`${V}/robots/robot-${n}.webp`}
          alt=""
          width={192}
          height={192}
          loading="lazy"
          decoding="async"
          draggable={false}
          className={`w-full h-auto drop-shadow-[0_10px_16px_rgba(0,0,0,0.5)] transition-all duration-700 ease-out ${
            active ? 'translate-y-0 opacity-100' : 'translate-y-[14%] opacity-0'
          }`}
          style={{ transitionDelay: active ? `${120 + (i % 6) * 45 + Math.floor(i / 6) * 90}ms` : '0ms' }}
        />
      ))}
    </div>
  </div>
);

/** A phone standing in the project's glow, its screen filled edge to edge. */
const PhoneFrame: React.FC<{ m: Media }> = ({ m }) => (
  <div className="absolute inset-0 grid place-items-center">
    <div className="h-[86%] aspect-[9/19.5] rounded-[clamp(18px,3.4vw,34px)] bg-black p-[2.2%] ring-1 ring-white/20 shadow-[0_40px_80px_-24px_rgba(0,0,0,0.85)]">
      <div
        className="relative h-full rounded-[clamp(14px,2.8vw,28px)] overflow-hidden bg-[#f7f3ee]"
        style={{ WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
      >
        <img
          src={m.src}
          alt={m.alt}
          width={m.w}
          height={m.h}
          loading="lazy"
          decoding="async"
          draggable={false}
          // a crop wider than a phone screen sits full-width under the island instead of being cut
          className={
            m.h / m.w > 1.9 ? 'absolute inset-0 w-full h-full object-cover object-top' : 'absolute top-[6%] left-0 w-full h-auto'
          }
        />
        <span aria-hidden="true" className="absolute top-[1.8%] left-1/2 -translate-x-1/2 w-[30%] h-[3.4%] rounded-full bg-black" />
      </div>
    </div>
  </div>
);

const SlideFill: React.FC<{ m: Media; active: boolean; first: boolean }> = ({ m, active, first }) => {
  if (m.fit === 'robots') return <RobotWall alt={m.alt} active={active} />;
  if (m.fit === 'phone') return <PhoneFrame m={m} />;
  if (m.fit === 'float')
    return (
      <div className="absolute inset-0 flex items-center justify-center p-[8%]">
        <img
          src={m.src}
          alt={m.alt}
          width={m.w}
          height={m.h}
          loading="lazy"
          decoding="async"
          draggable={false}
          className="max-w-full max-h-full w-auto h-auto rounded-[10px] md:rounded-[16px] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.75)] ring-1 ring-white/10"
        />
      </div>
    );
  return (
    <>
      <img
        src={m.src}
        alt={m.alt}
        width={m.w}
        height={m.h}
        loading={first ? 'eager' : 'lazy'}
        decoding="async"
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: m.position || 'center top' }}
      />
      {m.video && <SlideVideo m={m} active={active} />}
    </>
  );
};

/**
 * One screen at a time. It moves on by itself while it is on screen and the
 * pointer is elsewhere; arrows, the progress segments, a swipe or the arrow
 * keys take over. Every slide stays in the page, so each picture is there for
 * a crawler and nothing reloads when it comes back round.
 */
const Slideshow: React.FC<{ tool: Tool }> = ({ tool }) => {
  const slides = slidesOf(tool);
  const [i, setI] = useState(0);
  const [dir, setDir] = useState(1);
  const [held, setHeld] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: '-15% 0px' });
  const playing = inView && !held && !reducedMotion();
  const m = slides[i];

  const go = (n: number, d = n > i ? 1 : -1) => {
    setDir(d);
    setI((n + slides.length) % slides.length);
  };
  const next = () => go(i + 1, 1);
  const prev = () => go(i - 1, -1);

  useEffect(() => {
    if (!playing) return;
    const t = window.setTimeout(() => go(i + 1, 1), dwellOf(m) * 1000);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, i]);

  const n2 = (n: number) => String(n).padStart(2, '0');

  return (
    <div
      ref={ref}
      role="region"
      aria-roledescription="carousel"
      aria-label={`${tool.name} screenshots`}
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <motion.div
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
        }}
        drag="x"
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.12}
        dragSnapToOrigin
        onDragEnd={(_, info) => {
          if (info.offset.x < -50 || info.velocity.x < -400) next();
          else if (info.offset.x > 50 || info.velocity.x > 400) prev();
        }}
        data-cursor="DRAG"
        className="relative -mx-2 sm:mx-0 aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-[14px] md:rounded-[20px] ring-1 ring-white/10 outline-none focus-visible:ring-2 focus-visible:ring-electric cursor-grab active:cursor-grabbing"
        style={{ background: glow(tool.accent), touchAction: 'pan-y', WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
        initial={{ clipPath: 'inset(100% 0 0 0)' }}
        whileInView={{ clipPath: 'inset(0% 0 0 0)' }}
        viewport={{ once: true, margin: '-8% 0px' }}
        transition={{ duration: 1.1, ease: EASE_IN_OUT }}
      >
        {slides.map((s, si) => {
          const on = si === i;
          return (
            <motion.figure
              key={s.src}
              aria-hidden={!on}
              className="absolute inset-0 m-0"
              style={{ background: !s.fit || s.fit === 'cover' ? '#12141d' : 'transparent', pointerEvents: on ? 'auto' : 'none' }}
              initial={false}
              animate={{ opacity: on ? 1 : 0, scale: on ? 1 : 1.03, x: on ? '0%' : `${dir * -2}%` }}
              transition={{ duration: on ? 0.9 : 0.6, ease: EASE_OUT }}
            >
              <SlideFill m={s} active={on} first={si === 0} />
              <figcaption className="sr-only">{s.caption}</figcaption>
            </motion.figure>
          );
        })}
      </motion.div>

      {/* progress: one segment per slide, the current one filling while it plays */}
      <div className="mt-[3px] md:mt-[7px] -mx-[3px] flex" role="group" aria-label="Choose a screenshot">
        {slides.map((s, si) => (
          <button
            key={s.src}
            type="button"
            onClick={() => go(si)}
            aria-label={`Show ${si + 1} of ${slides.length}: ${s.caption}`}
            aria-current={si === i}
            className="group/seg flex-1 h-11 px-[3px] flex items-center"
          >
            <span className="relative block w-full h-[2px] rounded-full bg-white/15 overflow-hidden transition-colors group-hover/seg:bg-white/30">
              {si < i && <span className="absolute inset-0 bg-white/55" />}
              {si === i &&
                (playing ? (
                  <motion.span
                    key={`fill-${i}`}
                    className="absolute inset-y-0 left-0 bg-electric"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: dwellOf(s), ease: 'linear' }}
                  />
                ) : (
                  <span className="absolute inset-0 bg-white" />
                ))}
            </span>
          </button>
        ))}
      </div>

      <div className="-mt-1 flex items-start gap-4 md:gap-6">
        <div className="flex-1 min-w-0 flex flex-col gap-1 sm:flex-row sm:gap-6">
        <span className="shrink-0 pt-0.5 font-grotesk text-xs text-white/55 tabular-nums" aria-hidden="true">
          {n2(i + 1)} / {n2(slides.length)}
        </span>
        <p className="flex-1 min-w-0 font-grotesk text-sm md:text-[15px] leading-snug text-white/80" aria-live="polite">
          <AnimatePresence mode="wait" initial={false}>
            <motion.span
              key={i}
              className="block"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.3, ease: EASE_OUT }}
            >
              {m.caption}
            </motion.span>
          </AnimatePresence>
        </p>
        </div>
        <div className="shrink-0 flex gap-2">
          {[
            { label: 'Previous screenshot', on: prev, Icon: ArrowLeft },
            { label: 'Next screenshot', on: next, Icon: ArrowRight },
          ].map(({ label, on, Icon }) => (
            <button
              key={label}
              type="button"
              onClick={on}
              aria-label={label}
              className="grid place-items-center w-11 h-11 rounded-full border border-white/15 text-white/80 hover:bg-white hover:text-ink hover:border-white transition-colors duration-300"
            >
              <Icon size={16} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * A case study as a spread: the words in a column, the slideshow beside them,
 * sides alternating down the section like an editorial layout.
 */
const ToolCard: React.FC<{ t: Tool; i: number }> = ({ t, i }) => {
  const [hovered, setHovered] = useState(false);
  const cardRef = useRef<HTMLElement>(null);
  // a phone never hovers: roll the title when the card reaches mid-screen
  const touchActive = useTouchActive(cardRef);
  const titleId = `tool-${t.id}-title`;
  const flip = i % 2 === 1;

  return (
    <article
      ref={cardRef}
      id={`tool-${t.id}`}
      aria-labelledby={titleId}
      data-surface="dark"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="rounded-[20px] md:rounded-[28px] bg-card text-white p-5 md:p-8 lg:p-10 overflow-hidden xl:grid xl:grid-cols-12 xl:grid-rows-[auto_1fr] xl:gap-x-12"
    >
      {/* one column of words beside the pictures on a wide screen; below that, words, pictures, words */}
      <div className={`xl:col-span-4 xl:row-start-1 ${flip ? 'xl:col-start-9' : 'xl:col-start-1'}`}>
        <span className="font-grotesk text-[11px] uppercase tracking-[0.22em] text-white/55">{t.kind}</span>
        <h3
          id={titleId}
          className="mt-3 font-grotesk font-semibold text-[2.6rem] md:text-5xl xl:text-[3.4rem] leading-[0.95] tracking-tight"
        >
          {/* rolled a word at a time, so a long name breaks between words and never inside one */}
          {t.name.split(' ').map((word, wi) => (
            <React.Fragment key={wi}>
              {wi > 0 && ' '}
              <span className="inline-block whitespace-nowrap">
                <LetterRoll text={word} active={hovered || touchActive} />
              </span>
            </React.Fragment>
          ))}
        </h3>
        <p className="mt-4 max-w-[44ch] font-grotesk text-base md:text-lg leading-snug text-white/75 [text-wrap:pretty]">{t.tagline}</p>
        <div className="mt-5 flex flex-wrap items-center gap-2">
          {t.link && (
            <a
              href={t.link.url}
              target="_blank"
              rel="noreferrer"
              data-cursor="VISIT"
              onClick={() => trackInteraction('project_click', `tool_${t.id}`)}
              className="mr-1 inline-flex items-center h-11 md:h-9 pointer-coarse:h-11 px-4 rounded-full bg-white text-ink font-grotesk text-[13px] font-medium hover:bg-electric hover:text-white transition-colors duration-300"
            >
              {t.link.label}
              <ArrowMark className="ml-2" />
            </a>
          )}
          <ul className="contents" aria-label="Platforms">
            {t.platforms.map((p) => {
              const { label, Icon } = PLATFORM[p];
              const ios = p === 'ios';
              return (
                <li
                  key={p}
                  className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full font-grotesk text-[10px] md:text-[11px] uppercase tracking-[0.12em] ${
                    ios ? 'bg-electric text-white' : 'border border-white/15 text-white/65'
                  }`}
                >
                  <Icon size={12} className={ios ? 'text-white' : 'text-white/45'} />
                  {label}
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      <div className={`mt-7 xl:mt-0 xl:col-span-8 xl:row-start-1 xl:row-span-2 ${flip ? 'xl:col-start-1' : 'xl:col-start-5'}`}>
        <Slideshow tool={t} />
      </div>

      <div className={`xl:col-span-4 xl:row-start-2 ${flip ? 'xl:col-start-9' : 'xl:col-start-1'}`}>
        <ul className="mt-8 xl:mt-10 flex flex-col md:grid md:grid-cols-3 md:gap-x-8 xl:flex">
          {t.highlights.map((h) => (
            <li key={h.title} className="border-t border-white/10 py-4">
              <h4 className="font-grotesk text-base font-medium text-white">{h.title}</h4>
              <p className="mt-1 font-grotesk text-sm leading-relaxed text-white/65">{h.text}</p>
            </li>
          ))}
        </ul>
        <p className="mt-2 border-t border-white/10 pt-4 font-grotesk text-[11px] uppercase tracking-[0.2em] text-white/55">
          {t.footer}
        </p>
      </div>
    </article>
  );
};

/**
 * The index, kept in reach. Once the contents list at the top has scrolled
 * away and a project is on screen, a slim pill under the site's bar names all
 * four, marks the one in view, and takes you to any of them. It leaves before
 * the first project and after the last, and gets out of the way of the reel's
 * theatre like the rest of the chrome.
 */
const ProjectSwitcher: React.FC<{
  indexRef: React.RefObject<HTMLElement | null>;
  listRef: React.RefObject<HTMLElement | null>;
  hidden?: boolean;
  scrollTo?: (selector: string) => void;
}> = ({ indexRef, listRef, hidden, scrollTo }) => {
  const [indexOut, setIndexOut] = useState(false);
  const [listIn, setListIn] = useState(false);
  const [current, setCurrent] = useState(TOOLS[0].id);
  const rowRef = useRef<HTMLOListElement>(null);

  // Read from the layout on scroll rather than from IntersectionObserver: a
  // jump from the top of the page straight to a project carries the index from
  // below the screen to above it without it ever crossing, and an observer
  // would never hear about that.
  useEffect(() => {
    const index = indexRef.current;
    const list = listRef.current;
    if (!index || !list) return;
    let raf = 0;
    const read = () => {
      raf = 0;
      const vh = window.innerHeight;
      const mid = vh / 2;
      setIndexOut(index.getBoundingClientRect().bottom < 0);
      // on screen while the projects still fill the middle of the viewport
      const r = list.getBoundingClientRect();
      setListIn(r.top < vh * 0.7 && r.bottom > vh * 0.3);
      // the current project is the last one whose top has passed the middle line
      let id = TOOLS[0].id;
      for (const t of TOOLS) {
        const el = document.getElementById(`tool-${t.id}`);
        if (el && el.getBoundingClientRect().top < mid) id = t.id;
      }
      setCurrent(id);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(read);
    };
    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [indexRef, listRef]);

  // on a narrow phone the row scrolls: keep the current name in it
  useEffect(() => {
    const row = rowRef.current;
    const on = row?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!row || !on || row.scrollWidth <= row.clientWidth) return;
    row.scrollTo({ left: on.offsetLeft - (row.clientWidth - on.offsetWidth) / 2, behavior: reducedMotion() ? 'auto' : 'smooth' });
  }, [current]);

  const show = indexOut && listIn && !hidden;
  const still = reducedMotion();

  // portalled to <body>, so no transformed ancestor can trap the fixed pill
  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.nav
          aria-label="Jump to a project"
          className="fixed z-[66] top-[70px] md:top-[84px] left-1/2 -translate-x-1/2 max-w-[calc(100vw-24px)]"
          initial={{ opacity: 0, y: still ? 0 : -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: still ? 0 : -10 }}
          transition={{ duration: still ? 0.15 : 0.45, ease: EASE_OUT }}
        >
          <ol
            ref={rowRef}
            className="flex overflow-x-auto overscroll-x-contain rounded-full bg-white/80 backdrop-blur border border-ink/10 shadow-[0_10px_30px_-12px_rgba(10,10,20,0.35)] px-1.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {TOOLS.map((t) => {
              const on = t.id === current;
              return (
                <li key={t.id} className="shrink-0">
                  <a
                    href={`#tool-${t.id}`}
                    aria-current={on ? 'true' : undefined}
                    onClick={(e) => {
                      if (!scrollTo) return;
                      e.preventDefault();
                      scrollTo(`#tool-${t.id}-top`);
                    }}
                    className={`flex items-center gap-1.5 h-11 px-3 md:px-4 font-grotesk text-[13px] md:text-sm font-medium tracking-tight whitespace-nowrap transition-colors duration-300 ${
                      on ? 'text-electric' : 'text-ink/65 hover:text-ink'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`w-1 h-1 rounded-full bg-electric transition-opacity duration-300 ${on ? 'opacity-100' : 'opacity-0'}`}
                    />
                    {t.name}
                  </a>
                </li>
              );
            })}
          </ol>
        </motion.nav>
      )}
    </AnimatePresence>,
    document.body,
  );
};

const VibeCodingSection: React.FC<{ scrollTo?: (selector: string) => void; theater?: boolean }> = ({ scrollTo, theater }) => {
  const indexRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  return (
  <section id="vibecoding" aria-labelledby="vibecoding-title" className="relative py-section">
    <div className="max-w-[92rem] mx-auto px-5 md:px-10">
      <div className="grid grid-cols-12 gap-y-10 md:gap-x-10 lg:items-end mb-8 md:mb-12">
        <div className="col-span-12 lg:col-span-7">
          <h2
            id="vibecoding-title"
            className="font-grotesk font-semibold text-[11vw] md:text-[5.5vw] leading-none tracking-tight text-ink"
          >
            VibeCoding
          </h2>
          <p className="mt-6 md:mt-8 font-grotesk text-lg md:text-2xl leading-snug tracking-tight text-ink/75 max-w-[36ch] [text-wrap:balance]">
            Software I design and build around the work, with AI coding tools. All of it runs on a phone, and two
            ship as native iOS apps.
          </p>
        </div>

        {/* the index: a contents list for the four projects, and real anchors for anyone who links one */}
        <nav ref={indexRef} aria-label="VibeCoding projects" className="col-span-12 lg:col-span-5">
          <span className="block font-grotesk text-[10px] md:text-xs uppercase tracking-[0.3em] text-ink/65 mb-3">
            002 · Tools I've built
          </span>
          <ol className="border-b border-ink/15">
            {TOOLS.map((t) => (
              <li key={t.id} className="border-t border-ink/15">
                <a
                  href={`#tool-${t.id}`}
                  onClick={(e) => {
                    if (!scrollTo) return;
                    e.preventDefault();
                    scrollTo(`#tool-${t.id}-top`);
                  }}
                  className="group/idx flex items-center gap-4 py-3 md:py-3.5 font-grotesk text-ink"
                >
                  <span
                    className="flex-1 text-base md:text-lg font-medium transition-transform duration-500 group-hover/idx:translate-x-1.5"
                    style={{ transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)' }}
                  >
                    {t.name}
                  </span>
                  <span className="hidden sm:block text-xs uppercase tracking-[0.18em] text-ink/65">{t.kind}</span>
                  {t.platforms.includes('ios') && (
                    <span className="text-[10px] uppercase tracking-[0.15em] text-electric">iOS</span>
                  )}
                  <span
                    aria-hidden="true"
                    className="text-ink/35 transition-all duration-300 group-hover/idx:text-electric group-hover/idx:translate-y-0.5"
                  >
                    ↓
                  </span>
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </div>
      <div ref={listRef} className="flex flex-col gap-4 md:gap-6">
        {TOOLS.map((t, i) => (
          <div key={t.id} className="relative">
            {/* where a jump lands: far enough above the card that the site's bar and the
                project pill clear its title, and outside the fade so its offset never counts */}
            <span id={`tool-${t.id}-top`} aria-hidden="true" className="absolute left-0 -top-[124px] md:-top-[140px]" />
            <FadeIn delay={i * 0.04}>
              <ToolCard t={t} i={i} />
            </FadeIn>
          </div>
        ))}
      </div>
    </div>
    <ProjectSwitcher indexRef={indexRef} listRef={listRef} hidden={theater} scrollTo={scrollTo} />
  </section>
  );
};

export default VibeCodingSection;
